// Import from the leaflet-free osrmConfig file so this module (reachable from main through the
// hooks barrel via useGoogleMaps) doesn't drag leaflet into the main bundle.
import { DEFAULT_OSRM_CONFIG } from 'pages/DrivingDirection/osrmConfig';

/**
 * The one place the Google Maps JS API gets loaded.
 *
 * It must be loaded exactly once per page. Two <script> tags make Google log "You have included the
 * Google Maps JavaScript API multiple times on this page", and when the two copies disagree about
 * credentials the failing authentication takes everything down with it. That is what broke Street
 * View: App.js warms an API-key script on mount, while the leaflet map components each pulled a
 * second copy authenticated with the retired Premium Plan client id `gme-countertools`. Google
 * answered InvalidClientIdMapError, so the panorama was constructed but never allowed to render —
 * a blank half-screen and a TypeError from inside Google's own streetview.js.
 *
 * Resolves with `window.google.maps` (the same value the old `load-google-maps-api` package gave
 * its callers), so the map components can keep using their existing `.then((googleMaps) => ...)`.
 */
const SCRIPT_SELECTOR = 'script[src*="maps.googleapis.com/maps/api/js"]';
const READY_POLL_MS = 100;

let loadingPromise = null;

// A script tag that is in the DOM but hasn't finished evaluating gives us no event to hook, so poll
// for the global it defines.
const resolveWhenReady = (resolve) => {
  const check = () => {
    if (window.google && window.google.maps) resolve(window.google.maps);
    else setTimeout(check, READY_POLL_MS);
  };
  check();
};

const loadGoogleMaps = () => {
  if (window.google && window.google.maps) return Promise.resolve(window.google.maps);
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve, reject) => {
    // Someone already put the script in the DOM — wait for it instead of adding a second copy.
    if (document.querySelector(SCRIPT_SELECTOR)) {
      resolveWhenReady(resolve);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${DEFAULT_OSRM_CONFIG.googleApiKey}&libraries=geometry&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // Clear the cache on settle: from here on the window.google check above is the fast path, and
      // a failed load can be retried by the next caller.
      loadingPromise = null;
      resolve(window.google && window.google.maps);
    };
    script.onerror = () => {
      loadingPromise = null;
      reject(new Error('Failed to load Google Maps'));
    };

    document.head.appendChild(script);
  });

  return loadingPromise;
};

export default loadGoogleMaps;
